import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styles from '../search.page.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { SectionComponent } from 'src/components/common/section/section.component';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { formatNumber } from 'shared/utils';
import { emptyBucket } from 'shared/utils/search-v2-mapper';
import { SearchV2BucketResults } from './search-v2-bucket-results.component';

const SECTION_TYPE_CONTACTS = 'contacts';
const SECTION_TYPE_WORKSPACE = 'workspace';

const CONTACT_TYPES = ['contact', 'users'];

// Search-v2: workspace buckets arrive from the API pre-merged across entity
// types and pre-sorted by score. This component renders them in API order —
// no client-side filterByWorkspace, no cross-type sort. `renderByType` is
// keyed by the API's entity type (knowledgeItems, labValues, admissions, ...).
const SearchV2Rendering = ({ results, renderByType, options }) => {
  const lang = useSelector(getLang('SEARCH_SCREEN'));
  const workspaces = useSelector(state => state.workspace).list;
  const selectedWorkspaceId = useSelector(state => state.workspace).selected;

  // Each bucket is `{ results, extended, total }`. SearchV2BucketResults pages the
  // exact hits and keeps `extended` behind its toggle at the end.
  const { workspaces: wsBuckets = {}, contacts = emptyBucket() } = results || {};

  const [userToggledSections, setUserToggledSections] = useState({});
  const handleSectionToggle = useCallback((wsId, open) => {
    setUserToggledSections(prev => ({ ...prev, [wsId]: open }));
  }, []);

  const sectionTypeOf = entry =>
    CONTACT_TYPES.includes(entry.type) ? SECTION_TYPE_CONTACTS : SECTION_TYPE_WORKSPACE;

  // A section with only fuzzy hits is labelled "related resources" rather than
  // "results", so the heading matches what expanding the toggle reveals.
  const sectionTitle = (name, { results: literal, extended }) =>
    literal.length > 0
      ? lang.SECTION_TITLE.replace('{name}', name).replace('{number}', formatNumber(literal.length))
      : lang.SECTION_WITHOUT_EXACT_MATCHES.replace('{name}', name).replace(
          '{number}',
          formatNumber(extended.length),
        );

  // ≤1 workspace: flat list, contacts first.
  if (workspaces.length <= 1) {
    const onlyWsId = workspaces[0]?.id;
    const bucket = onlyWsId ? wsBuckets[onlyWsId] || emptyBucket() : emptyBucket();
    const items = [...contacts.results, ...bucket.results];
    const extended = [...contacts.extended, ...bucket.extended];
    return (
      <ListItemsNavigator>
        <SearchV2BucketResults
          results={items}
          extended={extended}
          renderByType={renderByType}
          options={options}
          section={workspaces[0]?.name || ''}
          sectionType={sectionTypeOf}
          sectionPosition={1}
          sectionCollapsed={false}
          resetKey={results}
        />
      </ListItemsNavigator>
    );
  }

  const hasContacts = contacts.results.length > 0 || contacts.extended.length > 0;
  let sectionPositionCounter = hasContacts ? 1 : 0;
  const contactsSectionPosition = sectionPositionCounter;

  return (
    <div className={styles.marginTop}>
      {hasContacts && (
        <SectionComponent title={sectionTitle(lang.USERS, contacts)} defaultIsOpen={true}>
          <ListItemsNavigator className={styles.items}>
            <SearchV2BucketResults
              results={contacts.results}
              extended={contacts.extended}
              renderByType={renderByType}
              options={options}
              section={lang.USERS}
              sectionType={SECTION_TYPE_CONTACTS}
              sectionPosition={contactsSectionPosition}
              sectionCollapsed={false}
              resetKey={results}
            />
          </ListItemsNavigator>
        </SectionComponent>
      )}

      {workspaces
        .sort(({ id }) => (id === selectedWorkspaceId ? -1 : 1))
        .map(({ id, name }) => {
          const bucket = wsBuckets[id] || emptyBucket();
          const hasAnyHits = bucket.results.length > 0 || bucket.extended.length > 0;
          if (!hasAnyHits && selectedWorkspaceId !== id) return null;

          sectionPositionCounter++;
          const sectionPosition = sectionPositionCounter;
          const defaultOpen = hasAnyHits && id === selectedWorkspaceId;
          const isOpen = userToggledSections.hasOwnProperty(id)
            ? userToggledSections[id]
            : defaultOpen;

          return (
            <div key={`workspace-${id}`}>
              <SectionComponent
                title={sectionTitle(name, bucket)}
                isOpen={isOpen}
                onToggle={open => handleSectionToggle(id, open)}>
                <ListItemsNavigator className={styles.items}>
                  <SearchV2BucketResults
                    results={bucket.results}
                    extended={bucket.extended}
                    renderByType={renderByType}
                    options={options}
                    section={name}
                    sectionType={SECTION_TYPE_WORKSPACE}
                    sectionPosition={sectionPosition}
                    sectionCollapsed={!isOpen}
                    resetKey={results}
                  />
                </ListItemsNavigator>
              </SectionComponent>
            </div>
          );
        })}
    </div>
  );
};

export { SearchV2Rendering };
